import React, { useEffect, useState } from "react";
import { AiOutlineDownload } from "react-icons/ai";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import KlarnaLogo from "../../../../../../assets/klarna_logo/Klarna.svg";
import axios from "axios";
import { useCart } from "../../../../../../CartContext";
import { getDiscountInfo } from "../../../../../../utils/Discount"; // Import discount utility

// Assuming a standard VAT rate, adjust if yours is different
const VAT_RATE = 0.2; // 20% for UK VAT, for example

const PricingDropdown = () => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [productData, setProductData] = useState(null); // Renamed 'data' to 'productData' for clarity
  const [discountInfo, setDiscountInfo] = useState(null); // State for discount info
  const [loading, setLoading] = useState(true); // Add loading state
  const [error, setError] = useState(null); // Add error state
  const { addToCart } = useCart();
  const url = window.location.pathname;
  const detailPage = url.split("/").pop();

  useEffect(() => {
    const fetchProductAndDiscount = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`product/filter`, {
          params: {
            filter: "detailsPage",
            equal: `/${detailPage}`,
          },
        });
        const product = response.data.result[0];
        setProductData(product);

        // Fetch discount info for the product
        if (product?._id) {
          const discount = getDiscountInfo(product._id);
          setDiscountInfo(discount);
        }
      } catch (err) {
        console.error("Error fetching product or discount:", err);
        setError("Failed to load product details.");
      } finally {
        setLoading(false);
      }
    };

    fetchProductAndDiscount();
  }, [detailPage]); // Depend on detailPage to refetch if URL changes

  const toggleDropdown = () => {
    setDropdownOpen(!isDropdownOpen);
  };

  // Calculate prices and EMI plans based on fetched data and discount
  const originalPrice = productData?.price || 0;
  const hasDiscount = discountInfo?.discount > 0;
  const currentPriceBeforeVat = hasDiscount
    ? originalPrice * (1 - discountInfo?.discount / 100)
    : originalPrice;

  const currentPriceWithVat = currentPriceBeforeVat;
  const originalPriceWithVat = originalPrice;

  // Dynamically generated EMI Plans based on current price
  const emiPlans = [
    {
      months: "2 Months",
      divisor: 2,
      description: "Two equal monthly installments for flexibility.",
    },
    {
      months: "3 Months",
      divisor: 3,
      description: "Three equal monthly installments.",
    },
    {
      months: "6 Months",
      divisor: 6,
      description: "Six equal monthly installments.",
    },
  ].map((plan) => {
    const monthlyPriceBeforeVat = currentPriceBeforeVat / plan.divisor;
    const monthlyPriceWithVat = monthlyPriceBeforeVat * (1 + VAT_RATE);
    return {
      ...plan,
      price: `£${monthlyPriceWithVat.toFixed(2)}`, // Format price for display
    };
  });

  const handleAddToCart = (product) => {
    if (!product) return;
    const cartItem = { ...product }; // Shallow copy of the product data

    addToCart(cartItem);
  };

  if (loading) {
    return (
      <div className="bg-[#0C1524] text-gray-800 rounded-t-xl max-w-7xl mx-auto mt-10 p-8 relative flex justify-center items-center h-40">
        <p className="text-white">Loading pricing details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#0C1524] text-gray-800 rounded-t-xl max-w-7xl mx-auto mt-10 p-8 relative flex justify-center items-center h-40">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  // Handle case where productData might not be found
  if (!productData) {
    return <div className="display-none mb-4 "></div>;
  }

  return (
    <div className="bg-[#0C1524] text-gray-800 rounded-t-xl max-w-7xl mx-auto mt-10 p-8 relative">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-b pb-6">
        <div className="text-center sm:text-left">
          <div className="flex flex-col items-start">
            {hasDiscount && (
              <s className="text-gray-400 text-base">
                £{originalPriceWithVat?.toFixed(2)} + VAT
              </s>
            )}
            <span
              style={{
                color: "white",
                fontSize: "x-large",
                fontWeight: "bold",
              }}>
              £ {currentPriceWithVat.toFixed(2)} + VAT
            </span>
            {hasDiscount && (
              <div className="absolute top-0 right-0 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-tr-xl">
                {discountInfo?.discount}% OFF
              </div>
            )}
          </div>
          <button
            onClick={() => {
              handleAddToCart(productData); // Pass the raw productData to handleAddToCart
            }}
            className="bg-[#FF3131] text-white py-2 px-8 mt-4 rounded-lg text-base font-medium hover:bg-[#ff4d4d] transition-all">
            Add to Cart
          </button>
        </div>

        <div className="flex flex-col relative">
          {/* Klarna Section */}
          <div className="text-center">
            <img
              src={KlarnaLogo}
              alt="Klarna Logo"
              className="mx-auto w-[60px]"
            />
            <p className="mt-2 text-sm text-white">available on checkout</p>
          </div>
          {/* EMI Options Dropdown */}
          <div className="mt-6 relative">
            <button
              onClick={toggleDropdown}
              className="flex items-center justify-between w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-all z-20">
              <span className="text-sm font-medium">Monthly Payment Plans</span>
              {isDropdownOpen ? (
                <FiChevronUp size={20} />
              ) : (
                <FiChevronDown size={20} />
              )}
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 w-full bg-gray-800 rounded-lg mt-2 p-4 text-white shadow-lg z-10">
                {emiPlans.map((plan, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedPlan(plan.months)} // You might want to handle adding to cart here if selecting an EMI plan should update cart
                    className={`p-3 mb-2 rounded-lg cursor-pointer transition-all ${
                      selectedPlan === plan.months
                        ? "bg-[#FF3131]"
                        : "hover:bg-gray-700"
                    }`}>
                    <h3 className="text-md font-semibold">
                      {plan.price}/Month ({plan.months})
                    </h3>
                    <p className="text-sm">{plan.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <p className="text-base text-white font-medium">
            Course Specification
          </p>
          <a
            href="/course-specification.pdf"
            className="text-white hover:text-[#FF3131] transition-all"
            download>
            <AiOutlineDownload size={28} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default PricingDropdown;
